/**
 * ============================================================
 *  SLIDER OPTIMISER
 *  Patches Slick slider autoplay to be GPU & scroll friendly.
 *
 *  Problems it fixes:
 *  1. Slick's autoplay uses setInterval — fires even when the
 *     tab is hidden, the device is sleeping, or the slider is
 *     scrolled off-screen. Causes constant background CPU load
 *     and FPS dips during scroll (timer fires mid-frame).
 *
 *  2. No will-change on the slide track — the browser can't
 *     pre-promote the layer before the transition starts,
 *     causing a mid-animation composite layer promotion (jank).
 *
 *  3. No containment on slider wrappers — repaints from slides
 *     can propagate to surrounding DOM.
 *
 *  Solutions:
 *  - Replace setInterval with requestAnimationFrame-based timer
 *    (syncs to display refresh, skips hidden frames automatically)
 *  - Pause when slider scrolls off-screen (IntersectionObserver)
 *  - Pause when tab is hidden (visibilitychange)
 *  - Apply will-change: transform only during active transitions
 *  - Add CSS containment to slider wrappers
 * ============================================================
 */

(function () {
  'use strict';

  if (window.__SLIDER_OPTIMISER_LOADED__) return;
  window.__SLIDER_OPTIMISER_LOADED__ = true;

  /* ===========================================================
     CSS CONTAINMENT — inject once
     =========================================================== */
  const style = document.createElement('style');
  style.textContent = `
    /* Contain slider repaints so they don't cascade to the page */
    .slick-slider {
      contain: layout style;
    }
    /* Only apply will-change during active transition via JS class */
    .slick-slider.slick-animating .slick-track,
    .slick-slider.slick-animating .slick-list {
      will-change: transform;
    }
  `;
  document.head.appendChild(style);

  /* ===========================================================
     PATCH HELPER
     Replaces a single Slick instance's setInterval autoplay
     with an rAF-based version that respects visibility.
     =========================================================== */
  function patchSlickInstance(slick) {
    if (!slick || slick.__optimised__) return;
    slick.__optimised__ = true;

    let rafId        = null;
    let lastTick     = 0;
    let paused       = false;
    let offscreen    = false;
    let tabHidden    = false;

    /* -- Stop the original setInterval autoplay -- */
    if (slick.autoPlayTimer) {
      clearInterval(slick.autoPlayTimer);
      slick.autoPlayTimer = null;
    }

    /* -- Override Slick's own autoPlay / autoPlayClear methods -- */
    const originalAutoPlayClear = slick.autoPlayClear.bind(slick);

    slick.autoPlayClear = function () {
      stopRAF();
      originalAutoPlayClear();
    };

    slick.autoPlay = function () {
      stopRAF();
      if (slick.slideCount > slick.options.slidesToShow) {
        startRAF();
      }
    };

    /* -- rAF loop -- */
    function tick(now) {
      if (!slick.options || !slick.options.autoplay) { stopRAF(); return; }

      rafId = requestAnimationFrame(tick);

      // Skip if paused, off-screen, or tab hidden
      if (paused || offscreen || tabHidden || slick.interrupted) return;
      if (document.hidden) return;

      const interval = slick.options.autoplaySpeed || 3000;
      if (now - lastTick >= interval) {
        lastTick = now;
        slick.autoPlayIterator();
      }
    }

    function startRAF() {
      if (!slick.options || !slick.options.autoplay) return;
      stopRAF(); // prevent double-start
      lastTick = performance.now();
      rafId = requestAnimationFrame(tick);
    }

    function stopRAF() {
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
    }

    /* -- Pause/resume on tab visibility -- */
    function onVisibilityChange() {
      tabHidden = document.hidden;
      if (!tabHidden && !offscreen && !paused && slick.options.autoplay) {
        lastTick = performance.now(); // reset — don't fire immediately on return
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange);

    /* -- Pause/resume on scroll visibility (IntersectionObserver) -- */
    if ('IntersectionObserver' in window && slick.$slider && slick.$slider[0]) {
      const io = new IntersectionObserver((entries) => {
        offscreen = !entries[0].isIntersecting;
        if (!offscreen && !tabHidden && !paused && slick.options.autoplay) {
          lastTick = performance.now(); // reset after returning to view
        }
      }, { threshold: 0.1 });
      io.observe(slick.$slider[0]);
      slick.__optimiserIO__ = io;
    }

    /* -- Respect Slick's existing pause on hover -- */
    if (slick.$slider && slick.$slider[0]) {
      slick.$slider[0].addEventListener('mouseenter', () => { paused = true; });
      slick.$slider[0].addEventListener('mouseleave', () => {
        paused = false;
        lastTick = performance.now();
      });
      slick.$slider[0].addEventListener('touchstart', () => { paused = true; }, { passive: true });
      slick.$slider[0].addEventListener('touchend',   () => {
        paused = false;
        lastTick = performance.now();
      }, { passive: true });
    }

    /* -- Start if autoplay is on -- */
    if (slick.options.autoplay) {
      startRAF();
    }
  }

  /* ===========================================================
     WAIT FOR SLICK + PATCH ALL INSTANCES
     =========================================================== */
  function patchAll() {
    // jQuery / Slick must exist
    if (typeof jQuery === 'undefined') return;
    if (typeof jQuery.fn.slick === 'undefined') return;

    // Find all initialized Slick sliders on the page
    jQuery('[data-slick], .slick-initialized').each(function () {
      const slickData = jQuery(this).data('slick');
      if (slickData && slickData.autoPlayTimer !== undefined) {
        patchSlickInstance(slickData);
      }
    });

    // Also patch by hooking into the slick init event for future inits
    jQuery(document).off('init.slick.optimiser').on('init.slick.optimiser', '[data-slick], .slick-initialized', function (event, slick) {
      patchSlickInstance(slick);
    });
  }

  /* ===========================================================
     ALSO PATCH via Slick's own prototype so it catches
     instances initialized AFTER this script runs
     =========================================================== */
  function hookSlickPrototype() {
    if (typeof jQuery === 'undefined' || !jQuery.fn || !jQuery.fn.slick) return;

    const OrigSlick = jQuery.fn.slick;
    jQuery.fn.slick = function (options) {
      const result = OrigSlick.apply(this, arguments);

      // After init, patch each slider
      this.each(function () {
        const instance = jQuery(this).data('slick');
        if (instance) {
          // Small delay to let Slick finish setting up
          setTimeout(() => patchSlickInstance(instance), 100);
        }
      });

      return result;
    };
    jQuery.fn.slick.__optimiserPatched__ = true;
  }

  /* ===========================================================
     BOOT — try immediately and on DOMContentLoaded + load
     =========================================================== */
  function boot() {
    hookSlickPrototype();
    patchAll();
  }

  // Try multiple times as Slick may load after us
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  window.addEventListener('load', boot); // catch late-loading sliders

  // Extra safety: try again after 1.5s for lazily init'd sliders
  setTimeout(boot, 1500);

  console.log('%c🎠 Slider Optimiser loaded — rAF autoplay + IntersectionObserver pausing active', 'color:#63b3ed;font-size:11px;');

})();
